import React, { FC, useEffect, useMemo } from 'react';
import urlJoin from 'url-join';
import {
  Switch,
  Route,
  useLocation,
  useRouteMatch,
  Redirect,
  RouteComponentProps,
} from 'react-router';
import { ErrorBoundary } from 'react-error-boundary';
import { FragmentWrapper } from './FragmentWrapper';

import { reportError, reportEvent, useAnalytics } from 'Shared/analytics';
import { QUOTE_APP_ROUTE } from 'Shared/constants/routes';

import {
  AdvancedDriverAssistance,
  CheckoutLinkCongrats,
  CongratsAgent,
  Coverages,
  Drivers,
  InMoratorium,
  MaxDriversExceeded,
  NotAuthorized,
  OutOfMarket,
  PurchaseWrapper,
  ReportsWrapper,
  SoftStop,
  Start,
  Stopped,
  Summary,
  Underwriting,
  Vehicles,
} from 'QuoteApp/screens';
import { ErrorBoundaryPage, Header, RewardsTab } from 'QuoteApp/components';
import {
  AppProvider,
  useAppContext,
} from 'QuoteApp/services/providers/AppProvider';

import { HelpdeskAnchorContainer } from '../Helpdesk';
import styles from './styles.module.css';
import { useHistory } from 'react-router-dom';
import { GetDetailsResponseType } from 'QuoteApp/services/endpoints/GetDetails';
import {
  IN_PROGRESS,
  ResolveInProgressStatus,
} from '../ResolveInProgressStatus/ResolveInProgressStatus';
import { useHelpdeskControls } from 'Shared/hooks';
import { env } from 'Shared/constants/env';
import { hasDiscoveredDrivers } from 'QuoteApp/screens/Reports/CurrentCarrierReport';
import { RateComparison } from 'QuoteApp/screens/RateComparison';

export const applicationSteps = [
  {
    key: 'drivers',
    display: 'Drivers',
    path: '/drivers',
    component: Drivers,
  },
  {
    key: 'vehicles',
    display: 'Vehicles',
    path: '/vehicles',
    component: Vehicles,
  },
  {
    key: 'coverages',
    display: 'Coverages',
    path: '/coverages',
    component: Coverages,
  },
  {
    key: 'summary',
    display: 'Summary',
    path: '/summary',
    component: Summary,
  },
  {
    key: 'reports',
    display: 'Reports',
    path: '/reports',
    component: ReportsWrapper,
    canNavigateTo: (details: GetDetailsResponseType) => {
      return !details.quoteChangeAcknowledgement;
    },
  },
  {
    key: 'purchase',
    display: 'Purchase',
    path: '/purchase',
    component: PurchaseWrapper,
    canNavigateTo: (details: GetDetailsResponseType) => {
      return details.commands.bind.runnable;
    },
  },
];

export const maxDriversExceededReason = 'MoreThanFivePartiesWithExcluded';

export const Router: FC<RouteComponentProps> = () => {
  const { pathname } = useLocation();
  const { isUsingAda, isUsingStrada, widgetIsMessengerVersion } =
    useHelpdeskControls({
      zendeskVariant: 'quoteApp',
    });
  const currentStepIndex = applicationSteps.findIndex((s) =>
    pathname.includes(s.path)
  );
  const pathMatch = useRouteMatch<{
    appId: string;
    path?: string;
    reason?: string;
  }>(`${QUOTE_APP_ROUTE}/:appId/:path?/:reason?`);
  useAnalytics({
    insuranceApplicationId: pathMatch?.params.appId || '',
  });

  if (!pathMatch?.params.appId) {
    return null;
  }

  return (
    <FragmentWrapper>
      <HelpdeskAnchorContainer
        appId={pathMatch.params.appId}
        isUsingAda={isUsingAda}
        isUsingStrada={isUsingStrada}
        screen={pathMatch.params.path || ''}
        widgetIsMessengerVersion={widgetIsMessengerVersion}
      />
      <ErrorBoundary
        FallbackComponent={ErrorBoundaryPage}
        onError={(err) => {
          const ERROR_NAME = 'aqui-v2-error';
          const errorPayload = {
            insuranceApplicationId: pathMatch.params.appId,
            message: err.message,
          };
          reportError(err, ERROR_NAME, errorPayload);
          reportEvent(ERROR_NAME, errorPayload);
        }}
      >
        <AppProvider
          appId={pathMatch.params.appId}
          path={pathMatch.params.path || ''}
        >
          {({ isUnauthorized }) => (
            <ApplicationStateRedirects
              path={pathMatch.params.path || ''}
              reason={pathMatch.params.reason || ''}
            >
              <div className={styles.container} data-testid={pathMatch.url}>
                <Header currentStepIndex={currentStepIndex} />
                <RewardsTab />
                <Switch>
                  <Route
                    path={urlJoin(QUOTE_APP_ROUTE, '/:appId/not_authorized')}
                    component={NotAuthorized}
                    key="notAuthorized"
                  />

                  {!isUnauthorized && (
                    <FragmentWrapper>
                      <Route
                        path={urlJoin(QUOTE_APP_ROUTE, '/:appId/start')}
                        component={Start}
                        key="start"
                      />
                      {/* ADAS (Advanced Driver Assistance Systems) Route */}
                      {env.adasFeaturesEnabled && (
                        <Route
                          path={urlJoin(
                            QUOTE_APP_ROUTE,
                            '/:appId/advanced_driver_assistance'
                          )}
                          component={AdvancedDriverAssistance}
                          key="advancedDriverAssistance"
                        />
                      )}
                      {/* Stalled Routes */}
                      <Route
                        path={urlJoin(
                          QUOTE_APP_ROUTE,
                          '/:appId/stalled/soft_stop'
                        )}
                        component={SoftStop}
                        key="softStop"
                      />
                      <Route
                        path={urlJoin(
                          QUOTE_APP_ROUTE,
                          '/:appId/stalled/out_of_market'
                        )}
                        component={OutOfMarket}
                        key="outOfMarket"
                      />
                      <Route
                        path={urlJoin(
                          QUOTE_APP_ROUTE,
                          '/:appId/stalled/stopped'
                        )}
                        component={Stopped}
                        key="stopped"
                      />
                      <Route
                        path={urlJoin(
                          QUOTE_APP_ROUTE,
                          '/:appId/stalled/in_moratorium'
                        )}
                        component={InMoratorium}
                        key="inMoratorium"
                      />
                      <Route
                        path={urlJoin(
                          QUOTE_APP_ROUTE,
                          '/:appId/stalled/max_drivers_exceeded'
                        )}
                        component={MaxDriversExceeded}
                        key="maxDriversExceeded"
                      />
                      <Route
                        path={urlJoin(
                          QUOTE_APP_ROUTE,
                          '/:appId/congrats/:policyNumber?'
                        )}
                        component={CongratsAgent}
                        key="congrats"
                      />
                      <Route
                        path={urlJoin(
                          QUOTE_APP_ROUTE,
                          '/:appId/checkout-link-congrats'
                        )}
                        component={CheckoutLinkCongrats}
                        key="checkoutLinkCongrats"
                      />
                      <Route
                        path={urlJoin(
                          QUOTE_APP_ROUTE,
                          '/:appId/rate_comparison'
                        )}
                        component={RateComparison}
                        key="rateComparison"
                      />
                      <Route
                        path={urlJoin(QUOTE_APP_ROUTE, '/:appId/underwriting')}
                        component={Underwriting}
                        key="underwriting"
                      />
                      {applicationSteps.map((step) => (
                        <Route
                          path={urlJoin(QUOTE_APP_ROUTE, '/:appId', step.path)}
                          component={step.component}
                          key={step.key}
                        />
                      ))}
                    </FragmentWrapper>
                  )}
                </Switch>
              </div>
            </ApplicationStateRedirects>
          )}
        </AppProvider>
      </ErrorBoundary>
    </FragmentWrapper>
  );
};

const showMaxDriversExceeded = (details: GetDetailsResponseType) => {
  const appHasDiscoveredDrivers = hasDiscoveredDrivers(details);
  const isOnlyStoppedForMaxDriversExceeded =
    details.underwriting.underwritingStops.length && // .every returns true when the array is empty :(
    details.underwriting.underwritingStops.every(
      (stop) => stop.ruleName === maxDriversExceededReason
    );
  return appHasDiscoveredDrivers && isOnlyStoppedForMaxDriversExceeded;
};

const ApplicationStateRedirects: FC<{
  path: string;
  reason: string;
  children?: React.ReactNode;
}> = ({ children, path, reason }) => {
  const history = useHistory();
  const { appId, details, config } = useAppContext();

  const displaySoftStop = details.softStops?.some(
    (stop) => stop.displaySoftStop
  );

  const isCheckoutLinkEnabled = !!config?.checkoutLink?.enabled;
  const uwConsentRequired =
    !isCheckoutLinkEnabled &&
    details.underwritingConsent &&
    details.underwritingConsent.required;
  const uwConsentCollected =
    uwConsentRequired && !!details.underwritingConsent.givenAt;

  useEffect(() => {
    // One-time on page load redirects
    if (details.status !== 'open' || displaySoftStop) {
      // softStop and [dead, stop, out-of-market, bound] status redirects are always on and written elsewhere
      return;
    }

    const hasGaragingAddress =
      details.address[details.principalInsuredId].state.value;

    switch (path) {
      case 'start':
        if (hasGaragingAddress) {
          history.replace(urlJoin(QUOTE_APP_ROUTE, appId, '/summary'));
        }
        break;
      case 'drivers':
      case 'vehicles':
      case 'coverages':
      case 'summary':
        if (!hasGaragingAddress) {
          history.replace(urlJoin(QUOTE_APP_ROUTE, appId, '/start'));
        }
        break;
      case 'reports':
        if (!hasGaragingAddress) {
          history.replace(urlJoin(QUOTE_APP_ROUTE, appId, '/start'));
        }
        if (uwConsentRequired && !uwConsentCollected) {
          history.replace(urlJoin(QUOTE_APP_ROUTE, appId, '/underwriting'));
        }
        break;
      case 'purchase':
        if (!hasGaragingAddress) {
          history.replace(urlJoin(QUOTE_APP_ROUTE, appId, '/start'));
        } else if (!details.commands.bind.runnable) {
          history.replace(urlJoin(QUOTE_APP_ROUTE, appId, '/summary'));
        }
        break;
      case 'rate_comparison':
        break;
      case 'underwriting':
        if (uwConsentRequired) {
          // Stay on the page even if it's collected already
          history.replace(urlJoin(QUOTE_APP_ROUTE, appId, '/underwriting'));
        } else {
          history.replace(urlJoin(QUOTE_APP_ROUTE, appId, '/start'));
        }
        break;
      case 'checkout-link-congrats':
        break;
      case 'congrats':
      case 'stalled':
      case '':
      default:
        if (details.commands.bind.runnable) {
          history.replace(urlJoin(QUOTE_APP_ROUTE, appId, '/purchase'));
        } else if (hasGaragingAddress) {
          history.replace(urlJoin(QUOTE_APP_ROUTE, appId, '/summary'));
        } else {
          history.replace(urlJoin(QUOTE_APP_ROUTE, appId, '/start'));
        }
        break;
    }
    // Evaluate and correct initial page load for new vs continued applications
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const redirects = useMemo(() => {
    switch (details.status) {
      case 'bound':
        if (path !== 'congrats' && path !== 'checkout-link-congrats') {
          return <Redirect to={urlJoin(QUOTE_APP_ROUTE, appId, '/congrats')} />;
        }
        break;
      case 'dead':
      case 'stop':
        if (displaySoftStop) {
          return (
            <Redirect
              to={urlJoin(QUOTE_APP_ROUTE, appId, '/stalled/soft_stop')}
            />
          );
        }

        const stoppedPath = showMaxDriversExceeded(details)
          ? '/stalled/max_drivers_exceeded'
          : '/stalled/stopped';
        if (`/${path}/${reason}` !== stoppedPath) {
          return <Redirect to={urlJoin(QUOTE_APP_ROUTE, appId, stoppedPath)} />;
        }

        break;
      case 'in_moratorium':
        if (path !== 'stalled') {
          return (
            <Redirect
              to={urlJoin(QUOTE_APP_ROUTE, appId, '/stalled/in_moratorium')}
            />
          );
        }
        break;
      case 'out_of_market':
        if (path !== 'stalled') {
          return (
            <Redirect
              to={urlJoin(QUOTE_APP_ROUTE, appId, '/stalled/out_of_market')}
            />
          );
        }
        break;
      case 'open':
        if (path !== 'stalled' && displaySoftStop) {
          return (
            <Redirect
              to={urlJoin(QUOTE_APP_ROUTE, appId, '/stalled/soft_stop')}
            />
          );
        }

        if (path === 'stalled' && !displaySoftStop) {
          // Redirect to Summary if application reopens after removing undisclosed drivers
          if (reason === 'max_drivers_exceeded') {
            return (
              <Redirect
                to={{
                  pathname: urlJoin(QUOTE_APP_ROUTE, appId, '/summary'),
                  state: { applicationReopened: true },
                }}
              />
            );
          }

          if (uwConsentRequired && !uwConsentCollected) {
            return (
              <Redirect to={urlJoin(QUOTE_APP_ROUTE, appId, '/underwriting')} />
            );
          }

          // Redirect to Reports if application reopens after data challenging
          return <Redirect to={urlJoin(QUOTE_APP_ROUTE, appId, '/reports')} />;
        }

        if (path === 'reports' && uwConsentRequired && !uwConsentCollected) {
          return (
            <Redirect to={urlJoin(QUOTE_APP_ROUTE, appId, '/underwriting')} />
          );
        }

        if (path === 'underwriting') {
          if (uwConsentRequired) {
            // Stay on the page even if it's collected already
            return (
              <Redirect to={urlJoin(QUOTE_APP_ROUTE, appId, '/underwriting')} />
            );
          }

          return <Redirect to={urlJoin(QUOTE_APP_ROUTE, appId, '/start')} />;
        }
        break;
    }
    return null;
  }, [
    details,
    path,
    reason,
    displaySoftStop,
    appId,
    uwConsentRequired,
    uwConsentCollected,
  ]);

  if (IN_PROGRESS.includes(details.status)) {
    return <ResolveInProgressStatus />;
  }

  return (
    <FragmentWrapper>
      {redirects}
      {children}
    </FragmentWrapper>
  );
};
